import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Portal from "components/layout/Portal";
import CloseButton from "components/layout/CloseButton";
import StreamView from "components/streaming/StreamView";
import { useWebRTC } from "providers/WebRTCProvider";
import { streamDisplayLabel } from "lib/utils/connection";
import { ReactComponent as BottomRightCornerIcon } from "assets/svgs/navigation/map/bottomrightcorner.svg";
import { ReactComponent as SwapStreamsIcon } from "assets/svgs/streaming/SwapStreams.svg";

const MIN_W = 240;
const MIN_H = 180;
const DEFAULT_W = 360;
const DEFAULT_H = 240;
const CASCADE_OFFSET_PX = 28;
/** Keep the popup below the control top bar (`h-topBar` = 5rem). */
const TOP_BAR_CLEARANCE_PX = 88;
const MARGIN = 16;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const geometryStorageKey = (streamName) => `payload-stream-popup-geometry:${streamName}`;

const getDefaultGeometry = (offsetIndex = 0) => {
    const cascade = Math.max(0, offsetIndex) * CASCADE_OFFSET_PX;
    if (typeof window === "undefined") {
        return {
            x: MARGIN + cascade,
            y: TOP_BAR_CLEARANCE_PX + cascade,
            w: DEFAULT_W,
            h: DEFAULT_H,
        };
    }

    return {
        x: clamp(
            window.innerWidth - DEFAULT_W - MARGIN - cascade,
            MARGIN,
            Math.max(MARGIN, window.innerWidth - DEFAULT_W - MARGIN)
        ),
        y: clamp(
            TOP_BAR_CLEARANCE_PX + cascade,
            TOP_BAR_CLEARANCE_PX,
            Math.max(TOP_BAR_CLEARANCE_PX, window.innerHeight - DEFAULT_H - MARGIN)
        ),
        w: DEFAULT_W,
        h: DEFAULT_H,
    };
};

const readStoredGeometry = (streamName, offsetIndex = 0) => {
    const fallback = getDefaultGeometry(offsetIndex);
    if (typeof window === "undefined") return fallback;

    try {
        const raw = window.localStorage.getItem(geometryStorageKey(streamName));
        if (!raw) return fallback;

        const parsed = JSON.parse(raw);
        const w = clamp(Number(parsed.w) || DEFAULT_W, MIN_W, window.innerWidth - MARGIN * 2);
        const h = clamp(Number(parsed.h) || DEFAULT_H, MIN_H, window.innerHeight - MARGIN * 2);
        const maxX = Math.max(MARGIN, window.innerWidth - w - MARGIN);
        const maxY = Math.max(TOP_BAR_CLEARANCE_PX, window.innerHeight - h - MARGIN);

        return {
            x: clamp(Number(parsed.x) || fallback.x, MARGIN, maxX),
            y: clamp(Number(parsed.y) || fallback.y, TOP_BAR_CLEARANCE_PX, maxY),
            w,
            h,
        };
    } catch {
        return fallback;
    }
};

/**
 * Floating, draggable, resizable viewer for a pinned front/gimbal/thermal/arm stream.
 */
const PayloadStreamPopup = ({ streamName, onClose = () => {}, offsetIndex = 0 }) => {
    const { activeStream, streamDisplayNames, swapPinnedWithActive } = useWebRTC();
    const [geometry, setGeometry] = useState(() => readStoredGeometry(streamName, offsetIndex));
    const panelRef = useRef(null);
    const portalTarget = useMemo(
        () => (typeof document !== "undefined" ? document.body : null),
        []
    );

    const canSwap = Boolean(activeStream && streamName && activeStream !== streamName);

    useEffect(() => {
        if (typeof window === "undefined" || !streamName) return;
        window.localStorage.setItem(geometryStorageKey(streamName), JSON.stringify(geometry));
    }, [geometry, streamName]);

    const handleSwap = useCallback(() => {
        if (!canSwap) return;
        // Keep the window in place after remount under the new stream key.
        try {
            const raw = window.localStorage.getItem(geometryStorageKey(streamName));
            if (raw) {
                window.localStorage.setItem(geometryStorageKey(activeStream), raw);
            } else {
                window.localStorage.setItem(
                    geometryStorageKey(activeStream),
                    JSON.stringify(geometry)
                );
            }
        } catch {
            /* ignore */
        }
        swapPinnedWithActive(streamName);
    }, [activeStream, canSwap, geometry, streamName, swapPinnedWithActive]);

    const onDragHandlePointerDown = useCallback(
        (e) => {
            if (e.button !== 0) return;
            e.preventDefault();
            e.stopPropagation();

            const originX = geometry.x;
            const originY = geometry.y;
            const startClientX = e.clientX;
            const startClientY = e.clientY;

            const onMove = (ev) => {
                const margin = 8;
                const nx = originX + (ev.clientX - startClientX);
                const ny = originY + (ev.clientY - startClientY);
                const w = panelRef.current?.offsetWidth ?? geometry.w;
                const h = panelRef.current?.offsetHeight ?? geometry.h;
                const maxX = Math.max(margin, window.innerWidth - w - margin);
                const maxY = Math.max(TOP_BAR_CLEARANCE_PX, window.innerHeight - h - margin);

                setGeometry((prev) => ({
                    ...prev,
                    x: clamp(nx, margin, maxX),
                    y: clamp(ny, TOP_BAR_CLEARANCE_PX, maxY),
                }));
            };

            const onUp = () => {
                window.removeEventListener("pointermove", onMove);
                window.removeEventListener("pointerup", onUp);
                window.removeEventListener("pointercancel", onUp);
            };

            window.addEventListener("pointermove", onMove);
            window.addEventListener("pointerup", onUp);
            window.addEventListener("pointercancel", onUp);
        },
        [geometry.h, geometry.w, geometry.x, geometry.y]
    );

    const onResizePointerDown = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();

            const el = e.currentTarget;
            el.setPointerCapture(e.pointerId);
            const startX = e.clientX;
            const startY = e.clientY;
            const startW = geometry.w;
            const startH = geometry.h;

            const onMove = (ev) => {
                const margin = 8;
                const maxW = Math.round(window.innerWidth * 0.92);
                const maxH = Math.round(window.innerHeight * 0.8);
                const dw = ev.clientX - startX;
                const dh = ev.clientY - startY;
                const w = clamp(startW + dw, MIN_W, maxW);
                const h = clamp(startH + dh, MIN_H, maxH);
                const maxX = Math.max(margin, window.innerWidth - w - margin);
                const maxY = Math.max(TOP_BAR_CLEARANCE_PX, window.innerHeight - h - margin);

                setGeometry((prev) => ({
                    w,
                    h,
                    x: clamp(prev.x, margin, maxX),
                    y: clamp(prev.y, TOP_BAR_CLEARANCE_PX, maxY),
                }));
            };

            const onUp = () => {
                try {
                    el.releasePointerCapture(e.pointerId);
                } catch {
                    /* ignore */
                }
                window.removeEventListener("pointermove", onMove);
                window.removeEventListener("pointerup", onUp);
                window.removeEventListener("pointercancel", onUp);
            };

            window.addEventListener("pointermove", onMove);
            window.addEventListener("pointerup", onUp);
            window.addEventListener("pointercancel", onUp);
        },
        [geometry.h, geometry.w]
    );

    if (!streamName || !portalTarget) {
        return null;
    }

    return (
        <Portal visible target={portalTarget}>
            <div
                ref={panelRef}
                className="pointer-events-auto fixed z-[90] flex min-w-[15rem] flex-col overflow-hidden rounded-r10 border border-black80 bg-black90 shadow-lg"
                style={{
                    left: geometry.x,
                    top: geometry.y,
                    width: geometry.w,
                    height: geometry.h,
                }}
            >
                <div
                    className="flex shrink-0 cursor-grab touch-none select-none items-center gap-2 border-b border-black80 bg-black100/90 px-3 py-2 active:cursor-grabbing"
                    onPointerDown={onDragHandlePointerDown}
                >
                    <span className="min-w-0 flex-1 truncate text-h5 font-semibold text-white">
                        {streamDisplayLabel(
                            streamName,
                            streamDisplayNames?.get?.(streamName),
                        )}
                    </span>
                    <div
                        className="pointer-events-auto flex shrink-0 items-center gap-1"
                        onPointerDown={(e) => e.stopPropagation()}
                    >
                        {canSwap && (
                            <button
                                type="button"
                                aria-label={`Swap with ${activeStream}`}
                                title={`Swap with ${activeStream}`}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-r5 text-black60 hover:bg-black80 hover:text-primary2"
                                onClick={handleSwap}
                            >
                                <SwapStreamsIcon className="h-4 w-4" aria-hidden />
                            </button>
                        )}
                        <CloseButton
                            className="h-7 w-7 text-black60 hover:text-black40 [&_svg]:h-3.5 [&_svg]:w-3.5"
                            onClick={onClose}
                        />
                    </div>
                </div>

                <div className="relative min-h-0 flex-1 overflow-hidden bg-black100">
                    <StreamView
                        className="h-full w-full"
                        streamName={streamName}
                        touchFocusEnabled={false}
                    />
                </div>

                <button
                    type="button"
                    aria-label="Resize payload stream"
                    className="pointer-events-auto absolute bottom-0 right-0 z-[1] flex h-10 w-10 cursor-nwse-resize touch-none translate-x-1.5 translate-y-1.5 items-center justify-center p-0 text-black60 hover:text-primary2"
                    onPointerDown={onResizePointerDown}
                >
                    <BottomRightCornerIcon
                        className="pointer-events-none h-10 w-10 shrink-0 [&_path]:fill-current"
                        aria-hidden
                    />
                </button>
            </div>
        </Portal>
    );
};

export default PayloadStreamPopup;
